import { usePostFrontRichiesteIdModuliIdSezioniIdFileCodice } from '@framework/api-client';
import { IWidgetParsedProps, RichTextAdvancedRender } from '@framework/engine';
import { useField } from '@framework/form';
import { useMemo } from 'react';
import { useFillableSubmissionContext } from '../../../FillableSubmissionContext';
import { IUploadWidget } from '../IUploadWidget';
import { publicDataUploadWidgetName } from '../UploadWidget.type';
import {
  uploadWidgetFilenameField,
  uploadWidgetVerifyDocumentAiField,
} from '../createUploadWidgetFormSchema';
import { useUploadWidgetFileList } from './useUploadWidgetFileList';

interface IParams {
  widget: IUploadWidget;
  options: IWidgetParsedProps;
  /**
   * Se true, indica che l'input sarà per il file "pubblico" (direttiva omissis true),
   * quindi sostituisce i parametri con quelli della versione pubblica
   * (e.g. titolo, nome, label, help, ecc.)
   */
  publicUpload?: boolean;
}

export function useUploadWidgetTitle(
  widget: IUploadWidget,
  publicUpload?: boolean,
) {
  return useMemo(() => {
    if (publicUpload) {
      return widget.publicUploadTitle ? (
        <RichTextAdvancedRender text={widget.publicUploadTitle} />
      ) : undefined;
    }
    return widget.title ? (
      <RichTextAdvancedRender text={widget.title} />
    ) : undefined;
  }, [widget.title, widget.publicUploadTitle, publicUpload]);
}

export function getSectionIdFromFieldName(name: string) {
  return name.split('.')[0];
}

/** Data la lista di estensioni accettate, ritorna una lista di mimetype */
function getPermittedExtensions(
  fileTypes: string[] | null | undefined,
): string[] {
  if (!fileTypes) return [];
  return fileTypes
    .map(fileType => {
      if (fileType.startsWith('.')) {
        return fileType;
      }
      return `.${fileType}`;
    })
    .filter(Boolean);
}

const TO_MB = 1024 * 1024; // 1MB = 1024 KB = 1024*1024 B
/**
 * La dimensione massima di upload dei file non può superare i 100MB e deve essere sempre definita
 */
function getMaxFileSize(maxSize: number | null | undefined) {
  if (!maxSize) return 100 * TO_MB;
  return Math.min(maxSize, 100 * TO_MB);
}

/**
 * Hook che racchiude la logica per gestire l'upload di un file
 */
export function useUploadWidget(params: IParams) {
  const { widget, options, publicUpload } = params;

  const title = useUploadWidgetTitle(widget, publicUpload);
  const label = publicUpload ? widget.publicUploadLabel : options.label;
  const helpText = publicUpload ? widget.publicHelpText : options.helpText;

  const fieldName = publicUpload
    ? publicDataUploadWidgetName(options.name)
    : options.name;

  const filenameFieldName = uploadWidgetFilenameField(fieldName);
  const verifyDocumentAiFieldName =
    uploadWidgetVerifyDocumentAiField(fieldName);

  const fileIdField = useField(fieldName);
  const filenameField = useField(filenameFieldName);
  const verifyDocumentAiField = useField(verifyDocumentAiFieldName);

  const sectionId = getSectionIdFromFieldName(fieldName);
  const maxFileSize = getMaxFileSize(widget.rules?.maxDimension?.value);
  const maxFileSizeMessage: string | null =
    widget.rules?.maxDimension?.customMessage ?? null;

  const fileTypes: string[] = useMemo(
    () => getPermittedExtensions(widget.rules?.fileType?.value),
    [widget],
  );

  const fileTypesMessage: string | null =
    widget.rules?.fileType?.customMessage ?? null;

  const submissionContext = useFillableSubmissionContext();

  const upload = usePostFrontRichiesteIdModuliIdSezioniIdFileCodice({
    request: {
      headers: {
        ...(publicUpload ? { sensibile: true } : {}),
      },
    },
  });

  const { fileList, setFileList } = useUploadWidgetFileList(fieldName);

  return {
    title,
    label,
    helpText,
    fieldName,
    fileIdField,
    filenameField,
    verifyDocumentAiField,
    sectionId,
    maxFileSize,
    maxFileSizeMessage,
    fileTypes,
    fileTypesMessage,
    verifyDocumentAiFieldName,
    submissionId: submissionContext.submissionId,
    submissionModuleId: submissionContext.submissionModuleId,
    upload,
    fileList,
    setFileList,
  };
}
